import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

export default function SmallToggleRow({ checked, onChange, label, desc }) {
    return (
        <Box
            onClick={() => onChange(!checked)}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                py: '11px',
                borderBottom: '1px solid #EBEBEB',
                cursor: 'pointer',
                userSelect: 'none',
                '&:last-child': { borderBottom: 'none', pb: 0 },
                '&:first-of-type': { pt: 0 },
            }}
        >
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0D0D0D' }}>
                    {label}
                </Typography>
                {desc && (
                    <Typography sx={{ fontSize: 11.5, color: '#7A7A7A', lineHeight: 1.45, mt: '2px' }}>
                        {desc}
                    </Typography>
                )}
            </Box>
            <Box
                sx={{
                    flexShrink: 0,
                    mt: '2px',
                    width: 38,
                    height: 22,
                    borderRadius: '11px',
                    backgroundColor: checked ? '#00A76F' : '#EBEBEB',
                    position: 'relative',
                    transition: 'background 0.18s',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 3,
                        left: checked ? 19 : 3,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'left 0.18s',
                    }}
                />
            </Box>
        </Box>
    );
}

SmallToggleRow.propTypes = {
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    desc: PropTypes.string,
};

SmallToggleRow.defaultProps = {
    desc: '',
};

export function SmallToggleRowStandalone({ checked, onChange, label, desc, disabled, onLabelClick }) {
    const handleClick = () => {
        if (disabled) return;
        // If there's an onLabelClick and the toggle is already on, expand/collapse instead of toggling off
        if (onLabelClick && checked) {
            onLabelClick();
            return;
        }
        onChange(!checked);
    };

    const handleToggleClick = (e) => {
        if (disabled) return;
        e.stopPropagation();
        onChange(!checked);
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: '12px 14px',
                backgroundColor: checked ? '#F0FAF6' : '#F7F7F7',
                borderRadius: '12px',
                border: '1.5px solid',
                borderColor: checked ? '#00A76F' : '#EBEBEB',
                cursor: disabled ? 'not-allowed' : 'pointer',
                userSelect: 'none',
                transition: 'all 0.15s',
                opacity: disabled ? 0.5 : 1,
            }}
        >
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: disabled ? '#919EAB' : '#0D0D0D' }}>
                    {label}
                </Typography>
                {desc && (
                    <Typography sx={{ fontSize: 11.5, color: '#7A7A7A', lineHeight: 1.45, mt: '2px' }}>
                        {desc}
                    </Typography>
                )}
            </Box>
            <Box
                onClick={onLabelClick ? handleToggleClick : undefined}
                sx={{
                    flexShrink: 0,
                    mt: '2px',
                    width: 38,
                    height: 22,
                    borderRadius: '11px',
                    backgroundColor: checked ? '#00A76F' : '#EBEBEB',
                    position: 'relative',
                    transition: 'background 0.18s',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 3,
                        left: checked ? 19 : 3,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'left 0.18s',
                    }}
                />
            </Box>
        </Box>
    );
}

SmallToggleRowStandalone.propTypes = {
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    desc: PropTypes.string,
    disabled: PropTypes.bool,
    onLabelClick: PropTypes.func,
};

SmallToggleRowStandalone.defaultProps = {
    desc: '',
    disabled: false,
    onLabelClick: null,
};
